/**
 * AppRouter.jsx — ELYON-SHELL-FOUNDATION-1
 *
 * React Router DOM v7. Routes built from loaded manifests + lazy components.
 * Default route: / → first enabled module's defaultRoute, or /welcome if none.
 *
 * Shell is module-agnostic (§1.2 BINDING):
 * - Zero hard-coded module IDs or paths in this file
 * - Routes come entirely from manifests
 * - grep check: grep -r "'/tms'" src/shell/ → zero results ✓
 */
import { Suspense, lazy, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useModuleManifests } from './ModuleLoader.jsx';
import { Skeleton } from '../primitives/Skeleton/Skeleton.tsx';
import { EmptyState } from '../components/empty-state/EmptyState.jsx';

/** Fallback while a module screen is lazy-loading — Skeleton primitives. */
function ModuleLoadingFallback() {
  return (
    <div className="elyon-module-loading" aria-busy="true" aria-label="Loading module…">
      <Skeleton.Block width="100%" height={40} />
      <Skeleton.Line width="72%" />
      <Skeleton.Line width="55%" />
      <Skeleton.Block width="100%" height={120} />
    </div>
  );
}

/** Welcome screen — EmptyState component, no bare text. */
function WelcomeScreen() {
  return (
    <div className="elyon-no-modules">
      <EmptyState
        pattern="first-use"
        headline="Platform ready"
        body="No modules are enabled for this tenant. Once tenant configuration is provisioned, modules will appear in the sidebar automatically."
      />
    </div>
  );
}

/** Settings placeholder until SETTINGS module ships. */
function SettingsScreen() {
  return (
    <div className="elyon-settings-placeholder">
      <h1 className="elyon-settings-title">Settings</h1>
      <p className="elyon-settings-body">Platform settings — coming soon.</p>
    </div>
  );
}

export function AppRouter() {
  const { manifests, loading } = useModuleManifests();
  const location = useLocation();
  const navigate = useNavigate();

  // Build routes from manifests. Each screen in a manifest gets a Route.
  const moduleRoutes = useMemo(() => {
    if (!manifests.length) return [];

    return manifests.flatMap((manifest) => {
      const screens = manifest.screens ?? [];
      return screens.map((screen) => ({
        path: screen.path,
        Component: lazy(() =>
          screen.component().catch(() => ({
            default: () => (
              <div style={{ padding: 'var(--space-6)', color: 'var(--color-feedback-warning-text)' }}>
                Module screen unavailable.
              </div>
            ),
          }))
        ),
      }));
    });
  }, [manifests]);

  // Default redirect target
  const defaultRedirect = manifests.length > 0
    ? (manifests[0].defaultRoute ?? manifests[0].path ?? '/welcome')
    : '/welcome';

  // When the auth-retry fires and manifests arrive after the initial render
  // already committed to /welcome, bounce the user to the first module.
  // Without this, the sidebar updates (shows modules) but the main pane stays
  // stuck on /welcome because Routes only redirects on / or *.
  useEffect(() => {
    if (!loading && manifests.length > 0 && location.pathname === '/welcome') {
      navigate(defaultRedirect, { replace: true });
    }
  }, [loading, manifests.length, location.pathname, defaultRedirect, navigate]);

  if (loading) return <ModuleLoadingFallback />;

  return (
    <Suspense fallback={<ModuleLoadingFallback />}>
      <Routes>
        {/* Default redirect */}
        <Route path="/" element={<Navigate to={defaultRedirect} replace />} />

        {/* Welcome (no modules) */}
        <Route path="/welcome" element={<WelcomeScreen />} />

        {/* Settings */}
        <Route path="/settings" element={<SettingsScreen />} />
        <Route path="/settings/*" element={<SettingsScreen />} />

        {/* Module routes — built from manifests */}
        {moduleRoutes.map(({ path, Component }) => (
          <Route
            key={path}
            path={path}
            element={<Component />}
          />
        ))}

        {/* Wildcard → welcome */}
        <Route path="*" element={<Navigate to={defaultRedirect} replace />} />
      </Routes>
    </Suspense>
  );
}
